import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { CustomerStats, DateRange } from '../models/CustomerStats';

export type CustomerAnalyticsAggregateScope = {
  scopeType: 'store' | 'group' | 'global';
  scopeId: string;
};

type CustomerAnalyticsScopeDailyDoc = {
  dayKey?: string;
  userId?: string;
  totalOrders?: number;
  totalSpent?: number;
  cashOrders?: number;
  cardOrders?: number;
  deliveryOrders?: number;
  pickupOrders?: number;
  firstOrderAt?: unknown;
  lastOrderAt?: unknown;
  firstClientName?: string;
  firstClientPhone?: string;
  storeOrderCounts?: Record<string, unknown>;
};

type CustomerAnalyticsScopeStateDoc = {
  hasFullCoverage?: boolean;
};

type CallableCustomerAnalyticsStat = {
  userId?: string;
  clientName?: string;
  clientPhone?: string;
  totalOrders?: number;
  totalSpent?: number;
  averageOrderValue?: number;
  lastOrderAtMs?: number;
  firstOrderAtMs?: number;
  preferredPaymentMethod?: 'cash' | 'card';
  preferredDeliveryMethod?: 'delivery' | 'pickup';
  orderFrequency?: number;
  customerSince?: number;
  mostOrderedStore?: string;
};

type CustomerAggregateAccumulator = {
  userId: string;
  totalOrders: number;
  totalSpent: number;
  cashOrders: number;
  cardOrders: number;
  deliveryOrders: number;
  pickupOrders: number;
  firstOrderDate: Date | null;
  lastOrderDate: Date | null;
  clientName: string;
  clientPhone: string;
  storeOrderCounts: Map<string, number>;
};

class CustomerAnalyticsScopeDailyService {
  private readonly collectionName = 'customer_analytics_scope_daily';
  private readonly stateCollectionName = 'customer_analytics_scope_state';

  private normalizeNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private normalizeString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private buildDayKey(date: Date): string {
    const localDay = new Date(date);
    localDay.setHours(0, 0, 0, 0);
    return localDay.toISOString().slice(0, 10);
  }

  private toDate(value: unknown): Date | null {
    if (value instanceof Date) {
      return value;
    }

    if (
      value &&
      typeof value === 'object' &&
      'toDate' in value &&
      typeof (value as { toDate: () => Date }).toDate === 'function'
    ) {
      try {
        return (value as { toDate: () => Date }).toDate();
      } catch {
        return null;
      }
    }

    return null;
  }

  private createAccumulator(userId: string): CustomerAggregateAccumulator {
    return {
      userId,
      totalOrders: 0,
      totalSpent: 0,
      cashOrders: 0,
      cardOrders: 0,
      deliveryOrders: 0,
      pickupOrders: 0,
      firstOrderDate: null,
      lastOrderDate: null,
      clientName: '',
      clientPhone: '',
      storeOrderCounts: new Map<string, number>(),
    };
  }

  private mergeStoreCounts(
    accumulator: CustomerAggregateAccumulator,
    rawCounts: Record<string, unknown> | undefined
  ): void {
    if (!rawCounts || typeof rawCounts !== 'object') {
      return;
    }

    Object.entries(rawCounts).forEach(([storeId, count]) => {
      const normalizedStoreId = this.normalizeString(storeId);
      if (!normalizedStoreId) {
        return;
      }

      accumulator.storeOrderCounts.set(
        normalizedStoreId,
        (accumulator.storeOrderCounts.get(normalizedStoreId) || 0) +
          this.normalizeNumber(count)
      );
    });
  }

  private toCustomerStats(
    accumulator: CustomerAggregateAccumulator
  ): CustomerStats | null {
    if (
      accumulator.totalOrders <= 0 ||
      !accumulator.firstOrderDate ||
      !accumulator.lastOrderDate
    ) {
      return null;
    }

    let mostOrderedStore = '';
    let mostOrderedStoreCount = -1;
    accumulator.storeOrderCounts.forEach((count, storeId) => {
      if (count > mostOrderedStoreCount) {
        mostOrderedStore = storeId;
        mostOrderedStoreCount = count;
      }
    });

    const daysSinceFirst = Math.max(
      1,
      (accumulator.lastOrderDate.getTime() - accumulator.firstOrderDate.getTime()) /
        (1000 * 60 * 60 * 24)
    );
    const orderFrequency = (accumulator.totalOrders / daysSinceFirst) * 30;
    const customerSince = Math.max(
      1,
      (Date.now() - accumulator.firstOrderDate.getTime()) /
        (1000 * 60 * 60 * 24 * 30)
    );

    return {
      userId: accumulator.userId,
      clientName: accumulator.clientName || 'Client inconnu',
      clientPhone: accumulator.clientPhone || 'Non disponible',
      totalOrders: accumulator.totalOrders,
      totalSpent: accumulator.totalSpent,
      averageOrderValue: accumulator.totalSpent / accumulator.totalOrders,
      lastOrderDate: accumulator.lastOrderDate,
      firstOrderDate: accumulator.firstOrderDate,
      favoriteProducts: [],
      preferredPaymentMethod:
        accumulator.cashOrders >= accumulator.cardOrders ? 'cash' : 'card',
      preferredDeliveryMethod:
        accumulator.deliveryOrders >= accumulator.pickupOrders
          ? 'delivery'
          : 'pickup',
      orderFrequency,
      customerSince,
      mostOrderedStore,
      preferredDriver: undefined,
    };
  }

  private mapCallableCustomerStat(
    stat: CallableCustomerAnalyticsStat
  ): CustomerStats | null {
    const userId = this.normalizeString(stat.userId);
    const totalOrders = this.normalizeNumber(stat.totalOrders);
    const totalSpent = this.normalizeNumber(stat.totalSpent);
    const averageOrderValue = this.normalizeNumber(stat.averageOrderValue);
    const firstOrderAtMs = this.normalizeNumber(stat.firstOrderAtMs);
    const lastOrderAtMs = this.normalizeNumber(stat.lastOrderAtMs);

    if (!userId || totalOrders <= 0 || firstOrderAtMs <= 0 || lastOrderAtMs <= 0) {
      return null;
    }

    return {
      userId,
      clientName: this.normalizeString(stat.clientName) || 'Client inconnu',
      clientPhone: this.normalizeString(stat.clientPhone) || 'Non disponible',
      totalOrders,
      totalSpent,
      averageOrderValue,
      lastOrderDate: new Date(lastOrderAtMs),
      firstOrderDate: new Date(firstOrderAtMs),
      favoriteProducts: [],
      preferredPaymentMethod: stat.preferredPaymentMethod === 'cash' ? 'cash' : 'card',
      preferredDeliveryMethod:
        stat.preferredDeliveryMethod === 'pickup' ? 'pickup' : 'delivery',
      orderFrequency: this.normalizeNumber(stat.orderFrequency),
      customerSince: this.normalizeNumber(stat.customerSince),
      mostOrderedStore: this.normalizeString(stat.mostOrderedStore),
      preferredDriver: undefined,
    };
  }

  private async getCustomerStatsFromCollection(
    scope: CustomerAnalyticsAggregateScope,
    dateRange: DateRange
  ): Promise<CustomerStats[] | null> {
    const stateSnap = await getDoc(
      doc(db, this.stateCollectionName, `${scope.scopeType}__${scope.scopeId}`)
    );
    const stateData = stateSnap.data() as CustomerAnalyticsScopeStateDoc | undefined;
    if (stateData?.hasFullCoverage !== true) {
      return null;
    }

    const startDayKey = this.buildDayKey(dateRange.start);
    const endDayKey = this.buildDayKey(dateRange.end);

    const snap = await getDocs(
      query(
        collection(db, this.collectionName),
        where('scopeType', '==', scope.scopeType),
        where('scopeId', '==', scope.scopeId),
        where('dayKey', '>=', startDayKey),
        where('dayKey', '<=', endDayKey),
        orderBy('dayKey', 'asc')
      )
    );

    const customers = new Map<string, CustomerAggregateAccumulator>();

    snap.forEach((docSnap) => {
      const data = docSnap.data() as CustomerAnalyticsScopeDailyDoc;
      const userId = this.normalizeString(data.userId);
      if (!userId) {
        return;
      }

      const accumulator =
        customers.get(userId) || this.createAccumulator(userId);
      const firstOrderDate = this.toDate(data.firstOrderAt);
      const lastOrderDate = this.toDate(data.lastOrderAt);

      accumulator.totalOrders += this.normalizeNumber(data.totalOrders);
      accumulator.totalSpent += this.normalizeNumber(data.totalSpent);
      accumulator.cashOrders += this.normalizeNumber(data.cashOrders);
      accumulator.cardOrders += this.normalizeNumber(data.cardOrders);
      accumulator.deliveryOrders += this.normalizeNumber(data.deliveryOrders);
      accumulator.pickupOrders += this.normalizeNumber(data.pickupOrders);

      if (
        firstOrderDate &&
        (!accumulator.firstOrderDate ||
          firstOrderDate.getTime() < accumulator.firstOrderDate.getTime())
      ) {
        accumulator.firstOrderDate = firstOrderDate;
        accumulator.clientName = this.normalizeString(data.firstClientName);
        accumulator.clientPhone = this.normalizeString(data.firstClientPhone);
      }

      if (
        firstOrderDate &&
        accumulator.firstOrderDate &&
        firstOrderDate.getTime() === accumulator.firstOrderDate.getTime()
      ) {
        if (!accumulator.clientName) {
          accumulator.clientName = this.normalizeString(data.firstClientName);
        }
        if (!accumulator.clientPhone) {
          accumulator.clientPhone = this.normalizeString(data.firstClientPhone);
        }
      }

      if (
        lastOrderDate &&
        (!accumulator.lastOrderDate ||
          lastOrderDate.getTime() > accumulator.lastOrderDate.getTime())
      ) {
        accumulator.lastOrderDate = lastOrderDate;
      }

      this.mergeStoreCounts(accumulator, data.storeOrderCounts);
      customers.set(userId, accumulator);
    });

    return Array.from(customers.values())
      .map((accumulator) => this.toCustomerStats(accumulator))
      .filter((customer): customer is CustomerStats => customer !== null)
      .sort((a, b) => b.totalOrders - a.totalOrders);
  }

  async getCustomerStats(
    scope: CustomerAnalyticsAggregateScope,
    dateRange: DateRange | undefined
  ): Promise<CustomerStats[] | null> {
    if (!dateRange) {
      return null;
    }

    const startDayKey = this.buildDayKey(dateRange.start);
    const endDayKey = this.buildDayKey(dateRange.end);

    try {
      const callable = httpsCallable(functions, 'getCustomerAnalyticsScopeStats');
      const result = await callable({
        scopeType: scope.scopeType,
        scopeId: scope.scopeId,
        startDayKey,
        endDayKey,
      });
      const data = result.data as {
        success?: boolean;
        ready?: boolean;
        customers?: CallableCustomerAnalyticsStat[];
      };

      if (data.success === true && data.ready === true) {
        return (data.customers || [])
          .map((stat) => this.mapCallableCustomerStat(stat))
          .filter((customer): customer is CustomerStats => customer !== null)
          .sort((a, b) => b.totalOrders - a.totalOrders);
      }

      if (data.success === true && data.ready === false) {
        return null;
      }
    } catch (error) {
      console.warn('getCustomerAnalyticsScopeStats indisponible, fallback collection:', error);
    }

    return this.getCustomerStatsFromCollection(scope, dateRange);
  }

  async rebuildScopeIndex(scope: CustomerAnalyticsAggregateScope): Promise<{
    success: boolean;
    scannedOrders: number;
    processedDocs: number;
    writtenDocs: number;
    deletedDocs: number;
  }> {
    const callable = httpsCallable(functions, 'rebuildCustomerAnalyticsScopeDaily');
    const result = await callable(scope);
    const data = result.data as {
      success?: boolean;
      scannedOrders?: number;
      processedDocs?: number;
      writtenDocs?: number;
      deletedDocs?: number;
    };

    return {
      success: data.success === true,
      scannedOrders: Number(data.scannedOrders || 0),
      processedDocs: Number(data.processedDocs || 0),
      writtenDocs: Number(data.writtenDocs || 0),
      deletedDocs: Number(data.deletedDocs || 0),
    };
  }
}

const customerAnalyticsScopeDailyService =
  new CustomerAnalyticsScopeDailyService();
export default customerAnalyticsScopeDailyService;
